/** Контракты админ-аналитики заявок (admin/Bids/*) */

export type ChartGranularity = "hour" | "day" | "month";

export interface BidTimeStatistic {
    hour: string;
    totalCount: number;
    totalSum: number | bigint;
    totalBought: number;
    totalBoughtSum: number | bigint;
    csCount: number;
    csSum: number | bigint;
    csBought: number;
    csBoughtSum: number | bigint;
    dota2Count: number;
    dota2Sum: number | bigint;
    dota2Bought: number;
    dota2BoughtSum: number | bigint;
    tf2Count: number;
    tf2Sum: number | bigint;
    tf2Bought: number;
    tf2BoughtSum: number | bigint;
    giftCount: number;
    giftSum: number | bigint;
    giftBought: number;
    giftBoughtSum: number | bigint;
    rustCount: number;
    rustSum: number | bigint;
    rustBought: number;
    rustBoughtSum: number | bigint;
}

export interface BidFunnelStatistic {
    totalCreated: number;
    activeCount: number;
    completedCount: number;
    reservedQty: number;
    unfilledClosedQty: number;
}

export interface BidFunnelPoint extends BidFunnelStatistic {
    hour: string;
}

export interface BidFunnelReport {
    totals: BidFunnelStatistic;
    series: BidFunnelPoint[];
}

export interface BidFillRateStatistic {
    bidsCount: number;
    totalRequestedQty: number | bigint;
    totalBoughtQty: number | bigint;
    remainingQty: number | bigint;
    unfilledClosedQty: number | bigint;
    /** 0..1 */
    volumeFillRate: number;
    /** 0..1 */
    averageBidFillRate: number;
}

export interface BidFillRatePoint extends BidFillRateStatistic {
    hour: string;
}

export interface BidFillRateReport {
    totals: BidFillRateStatistic;
    series: BidFillRatePoint[];
}

export interface BidTopSkinInfo {
    marketSkinId: string;
    gameId: number;
    hashName: string;
    bidsCount: number;
    uniqueBuyersCount: number;
    totalCount: number;
    totalBought: number;
    maxPrice: number | bigint;
    totalSum: number | bigint;
}

export interface BidPriceSpreadInfo {
    marketSkinId: string;
    gameId: number;
    hashName: string;
    maxBidPrice: number | bigint;
    minSellPrice: number | bigint;
    /** maxBid − minSell */
    spread: number | bigint;
    remainingQty: number;
    activeBidsCount: number;
    hasSellOffer: boolean;
}

export interface BidBookDepthByGame {
    gameId: number;
    openBidsCount: number;
    remainingQty: number;
    reservedQty: number;
    openGmv: number | bigint;
}

export interface BidBookDepthStatistic {
    openBidsCount: number;
    remainingQty: number;
    reservedQty: number;
    openGmv: number | bigint;
    executableBidsCount: number;
    executableRemainingQty: number;
    executableOpenGmv: number | bigint;
    byGame: BidBookDepthByGame[];
}

export interface BidBookDepthPoint {
    hour: string;
    openBidsCount: number;
    remainingQty: number;
    reservedQty: number;
    openGmv: number | bigint;
    executableBidsCount: number;
    executableRemainingQty: number;
    executableOpenGmv: number | bigint;
}

export interface BidBookDepthReport {
    totals: BidBookDepthStatistic;
    series: BidBookDepthPoint[];
}

export interface BidTopBuyerInfo {
    userId: number | bigint;
    buyerName: string;
    bidsCount: number;
    totalSum: number | bigint;
    totalCount: number;
    totalBought: number;
}

export interface BidOrderConversionStatistic {
    bidsCreated: number;
    bidsWithOrders: number;
    /** 0..1 */
    bidToOrderRate: number;
    ordersFromBids: number;
    completedOrdersFromBids: number;
    failedOrdersFromBids: number;
    ordersSum: number | bigint;
    completedOrdersSum: number | bigint;
}

export interface BidOrderConversionPoint extends BidOrderConversionStatistic {
    hour: string;
}

export interface BidOrderConversionReport {
    totals: BidOrderConversionStatistic;
    series: BidOrderConversionPoint[];
}

/** Канал продажи по заявке (enum BidSaleChannel на бэке) */
export enum BidSaleChannel {
    Unknown = 0,
    AutoMatch = 1,
    ProductPage = 2,
    QuickSale = 3,
    ExternalApi = 4,
}

export interface BidSaleChannelVolume {
    channel: BidSaleChannel;
    ordersCount: number;
    /** Проданных скинов (позиций заказов) */
    itemsCount: number;
    /** Сумма, копейки */
    sum: number | bigint;
}

export interface BidSaleChannelTotal extends BidSaleChannelVolume {
    /** 0..1 */
    itemsShare: number;
    /** 0..1 */
    sumShare: number;
}

export interface BidSaleChannelPoint extends BidSaleChannelVolume {
    hour: string;
}

/** Итог офлайн-покупок за период (срез поверх каналов) */
export interface BidOfflineSaleTotal {
    ordersCount: number;
    /** Проданных скинов (позиций заказов) */
    itemsCount: number;
    /** Сумма, копейки */
    sum: number | bigint;
    /** Доля от всех продаж по заявкам по количеству, 0..1 */
    itemsShare: number;
    /** Доля от всех продаж по заявкам по сумме, 0..1 */
    sumShare: number;
}

export interface BidOfflineSalePoint {
    hour: string;
    ordersCount: number;
    itemsCount: number;
    /** Сумма, копейки */
    sum: number | bigint;
}

export interface BidSaleChannelReport {
    channels: BidSaleChannelTotal[];
    series: BidSaleChannelPoint[];
    /** Офлайн-покупки: заказы, созданные по офлайн-заявкам (нет у старого бэка) */
    offline?: BidOfflineSaleTotal;
    offlineSeries?: BidOfflineSalePoint[];
    /** Уникальные покупатели с офлайн-покупками */
    offlineBuyersCount?: number;
}

export enum BidRiskReason {
    StuckActive = 0,
    HighReserved = 1,
    InsufficientBalance = 2,
    BiddingDisabled = 3,
}

export interface BidRiskInfo {
    bidId: number;
    userId: number | bigint;
    buyerName: string;
    marketSkinId: string;
    price: number | bigint;
    count: number;
    bought: number;
    reserved: number;
    remaining: number;
    created: string;
    updated: string;
    riskReason: BidRiskReason;
    userRubles: number | bigint;
    bidingEnabled: boolean;
}

export interface BidsAnalyticsPeriodParams {
    from: Date;
    to: Date;
    gameId?: number;
}

export interface BidsAnalyticsTakeParams {
    take?: number;
    gameId?: number;
}

export interface BidsAnalyticsPeriodTakeParams extends BidsAnalyticsPeriodParams {
    take?: number;
}

export interface BidsAnalyticsRisksParams {
    take?: number;
    stuckHours?: number;
    gameId?: number;
}

export interface BidsAnalyticsBookDepthParams {
    gameId?: number;
    from?: Date;
    to?: Date;
}
